import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Loader2, Download, RefreshCw } from "lucide-react"
import type { ArtImageResponse } from "@/lib/api-types"
import { apiClient } from "@/lib/api-client"
import { useToast } from "@/hooks/use-toast"
import JSZip from "jszip"
import { saveAs } from "file-saver"

interface LuckyVariationsGridProps {
  jobId: string
  images: ArtImageResponse[]
  zipUrl?: string
  selectedVariations?: string[] // Nomes completos das variações selecionadas (ex: "MASCOTE LUCKY - ALAGOAS")
  onRegenerate?: () => void
}

// Função helper para extrair nome da variação do s3_key
function getVariationNameFromS3Key(s3Key: string): string {
  // Formato esperado: "art/{job_id}_{display_name}_{index}.png"
  // Exemplo: "art/123_alagoas_0.png"
  const parts = s3Key.split("_")
  if (parts.length >= 2) {
    // Pega a parte antes do último número (índice)
    const namePart = parts.slice(1, -1).join("_")
    // Converte para nome amigável (primeira letra maiúscula)
    return namePart.charAt(0).toUpperCase() + namePart.slice(1).replace(/_/g, " ")
  }
  return "Variação"
}

export function LuckyVariationsGrid({ jobId, images, zipUrl, selectedVariations, onRegenerate }: LuckyVariationsGridProps) {
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(null)
  const [isCreatingZip, setIsCreatingZip] = useState(false)
  const { toast } = useToast()

  // Função para converter display_name de volta para nome completo
  const getFullVariationName = (displayName: string): string | null => {
    if (!selectedVariations || selectedVariations.length === 0) return null
    
    // Procura a variação que corresponde ao display_name
    for (const fullName of selectedVariations) {
      // Remove prefixo "MASCOTE LUCKY - " e converte para minúsculas com underscores
      const normalizedFull = fullName.replace("MASCOTE LUCKY - ", "").toLowerCase().replace(/\s+/g, "_")
      const normalizedDisplay = displayName.toLowerCase().replace(/\s+/g, "_")
      
      if (normalizedFull === normalizedDisplay) {
        return fullName
      }
    }
    return null
  }

  const handleRegenerate = async (image: ArtImageResponse) => {
    const displayName = getVariationNameFromS3Key(image.s3_key)
    // Tenta encontrar o nome completo da variação
    const fullVariationName = getFullVariationName(displayName) || displayName
    
    if (!fullVariationName) {
      toast({
        title: "Erro",
        description: "Não foi possível identificar a variação completa",
        variant: "destructive",
      })
      return
    }
    
    setRegeneratingIndex(image.index)
    try {
      await apiClient.regenerateLuckyVariation(jobId, fullVariationName)
      toast({
        title: "Regenerando...",
        description: `Regenerando variação: ${displayName}`,
      })
      
      // Chama callback para atualizar a lista
      if (onRegenerate) {
        setTimeout(() => {
          onRegenerate()
        }, 2000) // Aguarda 2s antes de atualizar
      }
    } catch (error: any) {
      console.error("[LUCKY GRID] Erro ao regenerar:", error)
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível regenerar a variação",
        variant: "destructive",
      })
    } finally {
      setRegeneratingIndex(null)
    }
  }

  const handleDownloadZip = async () => {
    if (images.length === 0) {
      toast({
        title: "Erro",
        description: "Nenhuma imagem disponível para download",
        variant: "destructive",
      })
      return
    }

    setIsCreatingZip(true)
    try {
      toast({
        title: "Criando ZIP...",
        description: `Processando ${images.length} imagens...`,
      })

      const zip = new JSZip()
      let successCount = 0
      let errorCount = 0

      console.log('[LUCKY GRID] Iniciando download de', images.length, 'imagens')

      // Baixa todas as imagens e adiciona ao ZIP usando canvas (melhor para CORS)
      const downloadPromises = images.map(async (image, index) => {
        const variationName = getVariationNameFromS3Key(image.s3_key)
        const filename = `lucky_${variationName.toLowerCase().replace(/\s+/g, "_")}.png`

        console.log(`[LUCKY GRID] Processando imagem ${index + 1}/${images.length}: ${variationName}`)
        console.log(`[LUCKY GRID] URL: ${image.s3_url}`)

        if (!image.s3_url) {
          console.error(`[LUCKY GRID] URL não disponível para ${variationName}`)
          errorCount++
          return { success: false, error: 'URL não disponível' }
        }

        try {
          // Usa canvas para converter a imagem (contorna problemas de CORS)
          const img = new Image()
          img.crossOrigin = 'anonymous'
          
          // Carrega a imagem
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              console.log(`[LUCKY GRID] Imagem carregada: ${variationName}, dimensões: ${img.width}x${img.height}`)
              resolve()
            }
            img.onerror = (err) => {
              console.error(`[LUCKY GRID] Erro ao carregar imagem ${variationName}:`, err)
              reject(new Error(`Erro ao carregar imagem: ${variationName}`))
            }
            img.src = image.s3_url
          })
          
          // Converte para canvas e depois para blob
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          
          if (!ctx) {
            throw new Error('Não foi possível obter contexto do canvas')
          }
          
          ctx.drawImage(img, 0, 0)
          
          // Converte canvas para blob
          const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((blob) => {
              if (blob) {
                console.log(`[LUCKY GRID] Blob criado: ${filename}, tamanho: ${blob.size} bytes`)
                resolve(blob)
              } else {
                reject(new Error('Falha ao converter canvas para blob'))
              }
            }, 'image/png')
          })
          
          if (blob.size === 0) {
            throw new Error(`Blob vazio para ${variationName}`)
          }
          
          // Adiciona ao ZIP
          zip.file(filename, blob)
          successCount++
          console.log(`[LUCKY GRID] ✅ Imagem adicionada ao ZIP: ${filename}`)
          
          return { success: true, filename }
        } catch (error: any) {
          console.error(`[LUCKY GRID] ❌ Erro ao processar imagem ${index + 1} (${variationName}):`, error)
          errorCount++
          
          // Tenta fallback com fetch direto
          try {
            console.log(`[LUCKY GRID] Tentando fallback com fetch para ${variationName}...`)
            const response = await fetch(image.s3_url)
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`)
            }
            const blob = await response.blob()
            if (blob.size > 0) {
              zip.file(filename, blob)
              successCount++
              errorCount-- // Corrige contador
              console.log(`[LUCKY GRID] ✅ Imagem adicionada via fetch fallback: ${filename}`)
              return { success: true, filename }
            }
          } catch (fetchError) {
            console.error(`[LUCKY GRID] Fallback fetch também falhou:`, fetchError)
          }
          
          return { success: false, error: error.message }
        }
      })

      // Aguarda todas as imagens serem processadas
      await Promise.all(downloadPromises)

      console.log(`[LUCKY GRID] Processamento concluído: ${successCount} sucesso, ${errorCount} erros`)

      if (successCount === 0) {
        throw new Error("Nenhuma imagem foi adicionada ao ZIP")
      }

      // Gera o arquivo ZIP
      console.log('[LUCKY GRID] Gerando arquivo ZIP...')
      const zipBlob = await zip.generateAsync({ 
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      })

      console.log(`[LUCKY GRID] ZIP gerado: ${zipBlob.size} bytes`)

      if (zipBlob.size === 0) {
        throw new Error("ZIP gerado está vazio")
      }

      // Salva o arquivo
      const zipFilename = `lucky_variations_${jobId}.zip`
      saveAs(zipBlob, zipFilename)

      toast({
        title: "Sucesso!",
        description: `ZIP criado com ${successCount} de ${images.length} variações${errorCount > 0 ? ` (${errorCount} falharam)` : ''}`,
      })
    } catch (error: any) {
      console.error("[LUCKY GRID] Erro ao criar ZIP:", error)
      toast({
        title: "Erro",
        description: error.message || "Não foi possível criar o arquivo ZIP",
        variant: "destructive",
      })
    } finally {
      setIsCreatingZip(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Botão de download ZIP - sempre disponível quando há imagens */}
      {images.length > 0 && (
        <div className="flex justify-center">
          <Button
            onClick={handleDownloadZip}
            disabled={isCreatingZip}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            size="lg"
          >
            {isCreatingZip ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Criando ZIP...
              </>
            ) : (
              <>
                <Download className="mr-2 h-5 w-5" />
                Baixar ZIP com todas as variações
              </>
            )}
          </Button>
        </div>
      )}

      {/* Grid de variações */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map((image) => {
          const variationName = getVariationNameFromS3Key(image.s3_key)
          const isRegenerating = regeneratingIndex === image.index

          return (
            <Card
              key={image.id}
              className="p-4 border-purple-500/30 bg-gradient-to-br from-slate-900/95 to-purple-900/40 backdrop-blur-sm"
            >
              <div className="space-y-3">
                {/* Preview da imagem */}
                <div className="relative rounded-lg overflow-hidden border border-purple-500/30 aspect-square">
                  <img
                    src={image.s3_url || "/placeholder.svg"}
                    alt={variationName}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                  {isRegenerating && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                    </div>
                  )}
                </div>

                {/* Informações */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-purple-100 text-sm sm:text-base">
                    {variationName}
                  </h3>
                  <p className="text-xs text-purple-200/70">
                    {image.width} × {image.height}px
                  </p>
                </div>

                {/* Botão de regenerar */}
                <div className="flex justify-center">
                  <Button
                    onClick={() => handleRegenerate(image)}
                    disabled={isRegenerating}
                    variant="outline"
                    size="icon"
                    className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10"
                    title="Regenerar variação"
                  >
                    {isRegenerating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
